import {useMonaco} from '@wandb/weave/common/components/Monaco/react';
import type * as Monaco from 'monaco-editor';
import {useEffect, useState} from 'react';

import {useColorMode} from '../../../util/useColorMode';

/** Monaco theme name for light mode. Pass to the `theme` prop on an editor. */
const THEME_NAME = 'wandb-diff-viewer-theme';
/** Monaco theme name for dark mode. Pass to the `theme` prop on an editor. */
const THEME_NAME_DARK = `${THEME_NAME}-dark`;

/**
 * Custom Monaco language ID for rendering raw unified diffs. Lines
 * starting with `+` are tokenized as additions and lines starting
 * with `-` as removals, which the light/dark themes color accordingly.
 */
export const DIFF_LANG = 'wandb-diff-viewer-diff';

// Registers the light and dark diff-viewer themes with Monaco.
// defineTheme is idempotent, so it is safe to call this more than once.
function registerTheme(monaco: typeof Monaco) {
  monaco.editor.defineTheme(THEME_NAME, {
    base: 'vs',
    inherit: true,
    rules: [
      {token: `added.${DIFF_LANG}`, foreground: '#0B7052'},
      {token: `removed.${DIFF_LANG}`, foreground: '#A32740'},
    ],
    colors: {
      'editor.background': '#FFFFFF',
      'diffEditor.removedLineBackground': '#FFE6E6',
      'diffEditor.removedTextBackground': '#FFC7CA',
      'diffEditor.insertedLineBackground': '#E4F7EE',
      'diffEditor.insertedTextBackground': '#A1F0CB',
    },
  });
  monaco.editor.defineTheme(THEME_NAME_DARK, {
    base: 'vs-dark',
    inherit: true,
    rules: [
      {token: `added.${DIFF_LANG}`, foreground: '#5ED6A4'},
      {token: `removed.${DIFF_LANG}`, foreground: '#FF7A88'},
    ],
    colors: {
      'diffEditor.removedLineBackground': '#571C28',
      'diffEditor.removedTextBackground': '#782637',
      'diffEditor.insertedLineBackground': '#0F3D31',
      'diffEditor.insertedTextBackground': '#165745',
    },
  });
}

// Registers DIFF_LANG and its Monarch tokenizer with Monaco.
// languages.register and setMonarchTokensProvider are both idempotent,
// so it is safe to call this more than once.
function registerDiffLang(monaco: typeof Monaco) {
  monaco.languages.register({id: DIFF_LANG});
  monaco.languages.setMonarchTokensProvider(DIFF_LANG, {
    tokenPostfix: `.${DIFF_LANG}`,
    tokenizer: {
      root: [
        [/^\+.*/, 'added'], // Lines starting with + are additions
        [/^-.*/, 'removed'], // Lines starting with - are deletions
      ],
    },
  });
}

/**
 * Registers the patch viewer's Monaco themes and language, returning
 * `true` once registration has completed. Gate rendering of any patch
 * viewer editors on this — mounting an editor before it returns `true`
 * will cause Monaco to fail to find the theme or language.
 *
 * Call this once near the top of the component tree that contains
 * patch viewer editors.
 */
export function usePatchViewerThemeRegistration(): boolean {
  const monaco = useMonaco();
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    if (!monaco) {
      return;
    }
    registerTheme(monaco);
    registerDiffLang(monaco);
    setIsRegistered(true);
  }, [monaco]);

  return isRegistered;
}

/**
 * Returns the Monaco theme name for the current browser color scheme —
 * `THEME_NAME` in light mode, `THEME_NAME_DARK` in dark mode. Pass the
 * returned value to the `theme` prop on a Monaco editor.
 */
export function usePatchViewerTheme() {
  const {isDarkMode} = useColorMode();
  return isDarkMode ? THEME_NAME_DARK : THEME_NAME;
}
